import { FormStore } from 'form/reflux/form_store';
import FormActionFactory from 'form/models/action/form_action_factory';
import { FormAPI, BaseAPI } from 'lecom-api';
import { GridFormStore } from 'grid_form/reflux/grid_form_store';

import { getClassProperties } from 'utils/object_utils';

import { generateUUID } from 'utils/uuid';

/**
 * @class FormActionAPI
 * @extends BaseAPI
 */
export default class FormActionAPI extends BaseAPI {
	_formId;
	_gridId;
	_fieldId;
	_isFieldAction;
	_isGridField;

	/**
	 * @static
	 * @param {*} actionType
	 */
	static actionProperties(actionType) {
		const actionModelClass = FormActionFactory.getModelClass(actionType);
		return getClassProperties(actionModelClass)
		.filter((p) => !['id', 'formId'].includes(p));
	}

	/**
	 * @constructor
	 * @param {*} param0
	 */
	constructor({ id, storeId, formId, gridId, fieldId, applyMode = FormAPI.applyMode, isFieldAction = false, isGridField = false }) {
		super({ id, storeId, applyMode, storeClass: FormStore });
		this._formId = formId;
		this._gridId = gridId;
		this._fieldId = fieldId;
		this._isFieldAction = isFieldAction;
		this._isGridField = isGridField;

		FormActionAPI.actionProperties(this.get('type')).forEach((actionProperty) => {
			this[actionProperty] = this._propertyAccessor.bind(this, actionProperty);
		});
	}

	get formId() {
		return this._formId;
	}

	get gridId() {
		return this._gridId;
	}

	get fieldId() {
		return this._fieldId;
	}

	get isFieldAction() {
		return this._isFieldAction;
	}

	get isGridField() {
		return this._isGridField;
	}

	/**
	 *
	 * @param {*} properties
	 */
	get(...properties) {
		const propertyValues = this.isFieldAction
		? this.isGridField
		? this.storeClass.getGridFieldActionProperties(this.storeId, this.gridId, this.fieldId, this.id, ...properties)
		: this.storeClass.getFieldActionProperties(this.storeId, this.fieldId, this.id, ...properties)
		: this.storeClass.getActionProperties(this.storeId, this.id, ...properties);

		if (!properties || properties && !properties.length) {
			return propertyValues;
		}
		return (properties.length === 1) ? propertyValues[properties[0]] : this.getProperties(propertyValues, properties);
	}

	/**
	 *
	 * @param {*} properties
	 */
	set(properties) {
		const isInEagerMode = this._isInEagerMode();
		const storeActions = this.storeClass.listenables;
		const storeEvents = this.storeClass.events;

		if (this.type() === 'grid') {
			const storeItemGrid = GridFormStore.store[this.fieldId];

			properties['updateUUID'] = generateUUID();

			for (let key in properties)
				storeItemGrid[key] = properties[key];
		}
		else if (this.isFieldAction) {
			if (this.isGridField)
				FormAPI.form(this.storeId).grids(this.gridId).fields(this.fieldId).set({ updateUUID: 'updateUUID' });
			else
				FormAPI.form(this.storeId).fields(this.fieldId).set({ updateUUID: 'updateUUID' });
		}

		this.isFieldAction
		? this.isGridField
		? storeActions.setGridFieldActionProperties(this.storeId, this.gridId, this.fieldId, this.id, properties, isInEagerMode)
		: storeActions.setFieldActionProperties(this.storeId, this.fieldId, this.id, properties, isInEagerMode)
		: storeActions.setActionProperties(this.storeId, this.id, properties, isInEagerMode);

		return isInEagerMode ? this._buildEventPromise(storeEvents.SET_ACTION_PROPERTIES) : this;
	}

	/**
	 *
	 */
	execute() {
		const storeActions = this.storeClass.listenables;
		const storeEvents = this.storeClass.events;

		this.isFieldAction
		? this.isGridField
		? storeActions.executeGridFieldAction(this.storeId, this.gridId, this.fieldId, this.id)
		: storeActions.executeFieldAction(this.storeId, this.fieldId, this.id)
		: storeActions.executeAction(this.storeId, this.id);

		return this._buildEventPromise(storeEvents.EXECUTE_ACTION, storeEvents.EXECUTE_ACTION_ERROR);
	}

	/**
	 *
	 * @param {*} event
	 * @param {*} args
	 */
	publish(event, ...args) {
		const storeEventHandler = this.storeClass.eventHandler;
		return storeEventHandler.publish(event, this.storeId, this.id, ...args);
	}

	/**
	 *
	 */
	apply() {
		return super.apply(this.id);
	}

	/**
	 *
	 * @private
	 * @param {*} eventHandler
	 * @param {*} eventName
	 * @param {*} elementStoreId
	 * @param {*} elementActionId
	 * @param {*} eventArgs
	 */
	_eventHandlerWrapper(eventHandler, eventName, elementStoreId, elementActionId, ...eventArgs) {
		if(typeof eventHandler === 'function'
			&& elementStoreId === this.storeId
		&& elementActionId === this.id) {

			eventHandler(elementStoreId, elementActionId, ...eventArgs);
		}
	}
};
